import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { WORKSPACE_BY_ID, workspaceFor, workspaceNavigation } from "@alm/policy";
import { DESK_QUERY } from "@/components/app/agent-pane/use-agent-pane";
import { EmptyState } from "@/components/app/lists/empty-state";
import { useMedia } from "@/hooks/use-wide";
import { openPaneAgent } from "@/lib/workspace/agent-pane";
import { usePolicy } from "@/lib/policy";
import { lastPlaces } from "@/lib/workspace/workspace-memory";
import { useMaintenanceBuilding } from "@/routes/_authenticated/_maintenance/maintenance/-lib/use-maintenance-building";

interface DavidSearch {
  c?: string;
  /** A question to start from, put in the box and never sent: the person sends it. */
  ask?: string;
}

export const Route = createFileRoute("/_authenticated/ws/david/")({
  validateSearch: (search: Record<string, unknown>): DavidSearch => ({
    ...(typeof search.c === "string" && search.c ? { c: search.c } : {}),
    ...(typeof search.ask === "string" && search.ask ? { ask: search.ask.slice(0, 2000) } : {}),
  }),
  head: () => ({
    meta: [
      { title: "Ask David | People AI" },
      {
        name: "description",
        content: "Work orders, the parts shop and what to buy, by asking.",
      },
    ],
  }),
  component: DavidPage,
});

/** The maintenance screen to show beside David: the one last open in his workspace, else his
 * first. None for somebody who may talk to him but open none of them. */
function useScreenBeside(): string | null {
  const { navigation, me } = usePolicy();
  const last = lastPlaces(me.user.id).david;
  if (last && !last.startsWith("/ws/david") && workspaceFor(last, "david").id === "david") {
    return last;
  }
  const screens = workspaceNavigation(WORKSPACE_BY_ID.david, navigation);
  return screens.find((entry) => entry.id !== "maintenance-agent")?.path ?? null;
}

/** David's front door: his conversation in the right-hand pane, beside a maintenance screen. His
 * conversations are one building's, so with no building picked the pane opens on none. */
function DavidPage() {
  const { c, ask } = Route.useSearch();
  const navigate = useNavigate();
  const desk = useMedia(DESK_QUERY);
  const beside = useScreenBeside();
  const { facilityId } = useMaintenanceBuilding();

  useEffect(() => {
    if (!beside) return;
    openPaneAgent(
      "maintenance",
      desk,
      facilityId
        ? {
            threadKey: `maintenance:${facilityId}`,
            threadId: c,
            ask,
            arriving: true,
          }
        : { arriving: true },
    );
    void navigate({ to: beside, replace: true });
  }, [ask, beside, c, desk, facilityId, navigate]);

  if (beside) return null;
  return (
    <>
      <h1 className="sr-only">David</h1>
      <EmptyState
        title="Nothing of David's to open"
        description="Your role can talk to David but opens none of his screens. Ask an administrator for maintenance access at a building."
      />
    </>
  );
}
